import { useQuery } from '@apollo/client';
import Slider, {
  SliderUnit,
} from '@flight-workspace/common-client/src/components/slider/Range';
import classNames from 'classnames/bind';
import { days, get12MonthsFromToday } from 'common/utils';
import { useInfoParamsContext } from 'contexts/infoParams';
import {
  DefaultValue,
  allContinentList,
  defaultRecommendSearchParams,
  getRecommendParams,
  useRecommendParamsContext,
} from 'contexts/recommendParams';
import Checkbox from 'everywhere/common/filter/checkbox';
import Container from 'everywhere/common/filter/container';
import GET_RECOMMEND_FLIGHT_COUNT from 'everywhere/monthly/recommendSummary.gql';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { FilterModalSection } from '../types';
import styles from './index.module.scss';

const cx = classNames.bind(styles);

function getDurationDesc(min: number, max: number) {
  if (
    (!min || min === DefaultValue.MIN_DURATION) &&
    (!max || max === DefaultValue.MAX_DURATION)
  ) {
    return '편도 기준의 비행 소요 시간 입니다.';
  }
  return `${min || DefaultValue.MIN_DURATION}시간 ~ ${
    max || DefaultValue.MAX_DURATION
  }시간`;
}

function getRoundTripTimeDesc(min: number, max: number) {
  if (
    (!min || min === DefaultValue.MIN_ROUND_TRIP_TIME) &&
    (!max || max === DefaultValue.MAX_ROUND_TRIP_TIME)
  ) {
    return '출발일부터 귀국일까지 총 여행기간입니다.';
  }
  return `${min || DefaultValue.MIN_ROUND_TRIP_TIME}일 ~ ${
    max || DefaultValue.MAX_ROUND_TRIP_TIME
  }일`;
}

function getFareDesc(min: number, max: number) {
  if (
    (!min || min === DefaultValue.MIN_FARE) &&
    (!max || max === DefaultValue.MAX_FARE)
  ) {
    return '왕복 성인 1인 기준 가격입니다.';
  }
  return `${(min || DefaultValue.MIN_FARE) / 10000}만원 ~ ${
    (max || DefaultValue.MAX_FARE) / 10000
  }만원`;
}

interface IProps {
  onClose(): void;
}

export default function FilterModal({ onClose }: IProps) {
  const { setInfoSearchParams } = useInfoParamsContext();
  const { recommendSearchParams, setRecommendSearchParams } =
    useRecommendParamsContext();
  const {
    query: { departure },
  } = useRouter();
  const [searchParams, setSearchParams] = useState(recommendSearchParams);
  const [minDuration, maxDuration] = searchParams.duration.split(',');
  const [minRoundTripTime, maxRoundTripTime] =
    searchParams.roundTripTime.split(',');
  const [minFare, maxFare] = searchParams.fare.split(',');
  const [durationDesc, setDurationDesc] = useState(
    getDurationDesc(+minDuration, +maxDuration),
  );
  const [roundTripTimeDesc, setRoundTripTimeDesc] = useState(
    getRoundTripTimeDesc(+minRoundTripTime, +maxRoundTripTime),
  );
  const [fareDesc, setFareDesc] = useState(getFareDesc(+minFare, +maxFare));
  const { continents, months } = searchParams;

  const { data } = useQuery(GET_RECOMMEND_FLIGHT_COUNT, {
    variables: {
      ...getRecommendParams(searchParams),
      recommendOnly: true,
      scity: departure,
    },
  });

  function hasContinent(ids: string[]) {
    return ids.some(id => continents.has(id));
  }

  function continentHandler(ids?: string[]) {
    let nextContinents = new Set(continents);
    if (!ids) {
      nextContinents = new Set();
    } else if (!hasContinent(ids)) {
      ids.forEach(id => {
        nextContinents.add(id);
      });
    } else {
      ids.forEach(id => {
        nextContinents.delete(id);
      });
    }
    setSearchParams(prev => ({
      ...prev,
      bounds: undefined,
      continents: nextContinents,
      countries: '',
    }));
  }

  const count = data?.recommend?.count || 0;

  function monthHandler(select?: string) {
    setSearchParams(prev => {
      const updated = new Set(prev.months);
      if (!select) updated.clear();
      else if (updated.has(select)) updated.delete(select);
      else updated.add(select);
      return { ...prev, months: updated };
    });
  }

  function reset() {
    setSearchParams(defaultRecommendSearchParams);
  }

  function okHandler() {
    setRecommendSearchParams(searchParams);
    setInfoSearchParams(prev => ({ ...prev, isDirect: searchParams.isDirect }));
    onClose();
  }

  const monthRange = get12MonthsFromToday();

  return (
    <Container
      count={count}
      onConfirm={okHandler}
      onReset={reset}
      onClose={onClose}
    >
      <div id={FilterModalSection.MODAL_TOP} className={cx('FilterModal')}>
        <div id={FilterModalSection.DEPART_MONTH} className={cx('section')}>
          <h5 className={cx('title')}>출발월</h5>
          <div className={cx('sub')}>
            <i className="current">
              {days(monthRange[0], 'YYYYMM').format('YYYY년 M월')} ~{' '}
              {days(monthRange[11], 'YYYYMM').format('YYYY년 M월')}
            </i>
          </div>
          <div className={cx('checkboxes_everywhere')}>
            <Checkbox
              clickCode="monthall"
              isChecked={!months.size}
              label="전체"
              onClick={() => {
                monthHandler();
              }}
            />
            {monthRange.map((month, index) => (
              <Checkbox
                key={month}
                clickCode={`month${index + 1}`}
                isChecked={months.has(month)}
                label={days(month, 'YYYYMM').format('M월')}
                onClick={() => {
                  monthHandler(month);
                }}
              />
            ))}
          </div>
        </div>
        <div id={FilterModalSection.REGION} className={cx('section')}>
          <h5 className={cx('title')}>지역</h5>
          <div className={cx('checkboxes_everywhere')}>
            <Checkbox
              clickCode="cont0"
              isChecked={!continents.size}
              label="전체"
              onClick={() => {
                continentHandler();
              }}
            />
            {Array.from(allContinentList).map(([ids, name], index) => (
              <Checkbox
                key={ids.join(',')}
                clickCode={`cont${index + 1}`}
                isChecked={hasContinent(ids)}
                label={name}
                onClick={() => {
                  continentHandler(ids);
                }}
              />
            ))}
          </div>
        </div>
        <div id={FilterModalSection.REQUIRED_TIME} className={cx('section')}>
          <h5 className={cx('title')}>소요시간</h5>
          <div className={cx('sub')}>
            <button
              type="button"
              className={cx('direct')}
              aria-checked={searchParams.isDirect}
              data-event-nlog="click"
              data-event-area-code="rtf.durdirect"
              onClick={() => {
                setSearchParams(prev => ({
                  ...prev,
                  isDirect: !prev.isDirect,
                }));
              }}
            >
              직항만
            </button>
          </div>
          <div className={cx('slide')}>
            <span className={cx('current', 'as_duration')}>
              <b className={cx('value')}>{durationDesc}</b>
            </span>
            <Slider
              min={DefaultValue.MIN_DURATION}
              max={DefaultValue.MAX_DURATION}
              selectedRange={{ min: +minDuration, max: +maxDuration }}
              unit={SliderUnit.HOUR}
              setValue={({ min, max }: { min: number; max: number }) => {
                setDurationDesc(getDurationDesc(min, max));
                setSearchParams(prev => ({
                  ...prev,
                  duration: `${min || DefaultValue.MIN_DURATION},${
                    max || DefaultValue.MAX_DURATION
                  }`,
                }));
              }}
            />
          </div>
        </div>
        <div id={FilterModalSection.TRAVEL_PERIOD} className={cx('section')}>
          <h5 className={cx('title')}>여행기간</h5>
          <div className={cx('slide')}>
            <span className={cx('current', 'as_duration')}>
              <b className={cx('value')}>{roundTripTimeDesc}</b>
            </span>
            <div data-event-nlog="click" data-event-area-code="rtf.triplength">
              <Slider
                min={DefaultValue.MIN_ROUND_TRIP_TIME}
                max={DefaultValue.MAX_ROUND_TRIP_TIME}
                selectedRange={{
                  min: +minRoundTripTime,
                  max: +maxRoundTripTime,
                }}
                unit={SliderUnit.DATE}
                setValue={({ min, max }: { min: number; max: number }) => {
                  setRoundTripTimeDesc(getRoundTripTimeDesc(min, max));
                  setSearchParams(prev => ({
                    ...prev,
                    roundTripTime: `${min || DefaultValue.MIN_ROUND_TRIP_TIME},${
                      max || DefaultValue.MAX_ROUND_TRIP_TIME
                    }`,
                  }));
                }}
              />
            </div>
          </div>
        </div>
        <div id={FilterModalSection.PRICE} className={cx('section')}>
          <h5 className={cx('title')}>가격</h5>
          <div className={cx('slide')}>
            <span className={cx('current', 'as_price')}>
              <b className={cx('value')}>{fareDesc}</b>
            </span>
            <Slider
              min={DefaultValue.MIN_FARE}
              max={DefaultValue.MAX_FARE}
              selectedRange={{ min: +minFare, max: +maxFare }}
              unit={SliderUnit.TENTHOUSAND}
              setValue={({ min, max }: { min: number; max: number }) => {
                setFareDesc(getFareDesc(min, max));
                setSearchParams(prev => ({
                  ...prev,
                  fare: `${min || DefaultValue.MIN_FARE},${
                    max || DefaultValue.MAX_FARE
                  }`,
                }));
              }}
            />
          </div>
        </div>
      </div>
    </Container>
  );
}
